import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { properties } from "@/data/properties";

// Calculate property counts dynamically from actual data
const getPropertyCountByLocation = (searchTerms: string[]) => {
  return properties.filter(p => 
    searchTerms.some(term => p.location.toLowerCase().includes(term.toLowerCase()))
  ).length;
};

const destinations = [
  {
    name: "Monaco",
    region: "Monte Carlo & Larvotto",
    properties: getPropertyCountByLocation(["Monaco", "Monte Carlo"]),
    description: "The pinnacle of European luxury living with yacht-filled harbors and tax efficiency.",
  },
  {
    name: "Dubai",
    region: "Palm Jumeirah & Marina",
    properties: getPropertyCountByLocation(["Dubai", "UAE"]),
    description: "Architectural marvels and ultra-modern penthouses in the desert oasis.",
  },
  {
    name: "London",
    region: "Mayfair & Knightsbridge",
    properties: getPropertyCountByLocation(["London"]),
    description: "Timeless elegance in the world's most established luxury property market.",
  },
  {
    name: "Italy",
    region: "Amalfi Coast & Sardinia",
    properties: getPropertyCountByLocation(["Italy", "Amalfi", "Sardinia", "Tuscany", "Como", "Sicily", "Positano"]),
    description: "From cliffside villas overlooking Positano to Costa Smeralda estates of distinction.",
  },
  {
    name: "Spain",
    region: "Marbella & Costa del Sol",
    properties: getPropertyCountByLocation(["Spain", "Marbella", "Ibiza", "Barcelona", "Mallorca"]),
    description: "Mediterranean elegance meets contemporary luxury in Costa del Sol.",
  },
  {
    name: "France",
    region: "Côte d'Azur & Paris",
    properties: getPropertyCountByLocation(["France"]),
    description: "From Haussmann grandeur in Paris to sun-drenched Riviera estates.",
  },
  {
    name: "Abu Dhabi",
    region: "Saadiyat & Yas Island",
    properties: getPropertyCountByLocation(["Abu Dhabi"]),
    description: "Cultural capital of the Gulf with world-class residences and waterfront living.",
  },
  {
    name: "KSA",
    region: "Riyadh & NEOM",
    properties: getPropertyCountByLocation(["Saudi Arabia", "KSA", "Riyadh", "Jeddah", "NEOM", "AlUla"]),
    description: "The Kingdom's Vision 2030 is reshaping luxury living with unprecedented developments.",
  },
  {
    name: "Hong Kong",
    region: "The Peak & Repulse Bay",
    properties: getPropertyCountByLocation(["Hong Kong"]),
    description: "Asia's premier financial hub with the world's most prestigious addresses.",
  },
  {
    name: "USA",
    region: "New York & Beverly Hills",
    properties: getPropertyCountByLocation(["USA", "New York", "Malibu", "Palm Beach", "Aspen", "Beverly Hills"]),
    description: "Iconic American luxury from Manhattan penthouses to Malibu oceanfront estates.",
  },
  {
    name: "Switzerland",
    region: "Geneva & St. Moritz",
    properties: getPropertyCountByLocation(["Switzerland"]),
    description: "Alpine grandeur meets banking prestige in the world's most discreet luxury market.",
  },
  {
    name: "Australia",
    region: "Sydney & Gold Coast",
    properties: getPropertyCountByLocation(["Australia"]),
    description: "Harbour-side mansions and coastal retreats in the Southern Hemisphere's crown jewels.",
  },
  {
    name: "Qatar",
    region: "The Pearl & Lusail",
    properties: getPropertyCountByLocation(["Qatar", "Doha"]),
    description: "Post-World Cup renaissance with extraordinary architectural ambition.",
  },
  {
    name: "Macau",
    region: "Cotai & Coloane",
    properties: getPropertyCountByLocation(["Macau"]),
    description: "Asia's entertainment capital with exclusive waterfront and hilltop residences.",
  },
  {
    name: "Israel",
    region: "Tel Aviv & Caesarea",
    properties: getPropertyCountByLocation(["Israel"]),
    description: "Innovation nation's luxury market with Mediterranean coastline and historic grandeur.",
  },
];

export const DestinationsSection = () => {
  return (
    <section id="destinations" className="py-24 lg:py-32 bg-secondary">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-gold mb-4">
            Global Presence
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground max-w-2xl">
            Markets We
            <span className="block italic font-medium text-primary">Know Intimately</span>
          </h2>
        </motion.div>

        {/* Destinations List */}
        <div className="space-y-0">
          {destinations.map((destination, index) => (
            <motion.div
              key={destination.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
            >
            <Link
              to={`/properties?location=${encodeURIComponent(destination.name)}`}
              className="group block py-6 lg:py-8 border-t border-border last:border-b hover:bg-secondary/50 transition-colors duration-300 -mx-6 px-6 lg:-mx-12 lg:px-12"
            >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-8">
                  {/* Left: Name and Arrow */}
                  <div className="flex items-center gap-4 lg:gap-8 lg:w-1/3">
                    <span className="font-body text-sm text-muted-foreground w-8">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-display text-2xl lg:text-3xl xl:text-4xl text-foreground group-hover:text-gold transition-colors duration-300">
                      {destination.name}
                    </h3>
                    <ArrowUpRight
                      size={20}
                      className="text-muted-foreground group-hover:text-gold transition-colors duration-300"
                    />
                  </div>

                  {/* Center: Region and Description */}
                  <div className="lg:w-1/3 pl-12 lg:pl-0">
                    <p className="font-body text-sm uppercase tracking-[0.15em] text-gold mb-1">
                      {destination.region}
                    </p>
                    <p className="font-body text-muted-foreground text-sm leading-relaxed group-hover:text-foreground transition-colors duration-300 line-clamp-2">
                      {destination.description}
                    </p>
                  </div>

                  {/* Right: Properties Count */}
                  <div className="pl-12 lg:pl-0 lg:text-right lg:w-1/3">
                    <span className="font-display text-3xl lg:text-4xl text-foreground group-hover:text-gold transition-colors duration-300">
                      {destination.properties}
                    </span>
                    <span className="font-body text-sm text-muted-foreground ml-2">
                      properties
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
